import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { SkeletonCard } from "@/components/skeleton";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency, formatDateTime } from "@/lib/i18n";

interface WebinarReport {
  webinarId: string;
  title: string;
  generatedAt: string;
  durationSeconds: number;
  attendanceFunnel: {
    registered: number;
    joined: number;
    stayed50Percent: number;
    stayedToEnd: number;
  };
  peakEngagementMoment: {
    timestamp: string | null;
    attendeeCount: number;
    chatCount: number;
    pollVotes: number;
    reactions: number;
    score: number;
    summary: string;
  } | null;
  ctaPerformance: Array<{
    ctaId: string;
    label: string;
    type: string;
    clicks: number;
    conversions: number;
    revenueCents: number;
  }>;
  topQuestions: Array<{
    id: string;
    question: string;
    askedBy: string;
    upvotes: number;
    answered: boolean;
  }>;
  moderationSummary?: {
    totalHeld: number;
    approved: number;
    rejected: number;
    undone: number;
    attendeeReports: {
      open: number;
      resolved: number;
      dismissed: number;
    };
    reasonCodeCounts: Record<string, number>;
    classifierFallbackTriggered: number;
    raidEpisodes: number;
    topActors: Array<{ actorId: string; actions: number }>;
  };
  recommendedFollowUps: string[];
}

interface CanvasArtifactTimelineEntry {
  id: string;
  boardTitle: string;
  boardAudience: "presenter" | "shared";
  summary: string;
  createdBy: string;
  createdAt: string;
  timestampSeconds: number | null;
  interactionCount: number;
}

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

function MetricCard({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      {detail ? <p className="mt-1 text-xs text-muted-foreground">{detail}</p> : null}
    </div>
  );
}

export default function WebinarReportPage() {
  const { webinarId } = useParams();
  const [report, setReport] = useState<WebinarReport | null>(null);
  const [canvasArtifacts, setCanvasArtifacts] = useState<CanvasArtifactTimelineEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!webinarId) return;
    setIsLoading(true);
    setError(null);

    Promise.all([
      apiFetch<{ data: WebinarReport }>(`/v1/webinars/${webinarId}/report`),
      apiFetch<{ data: { timeline: CanvasArtifactTimelineEntry[] } }>(`/v1/webinars/${webinarId}/canvas/artifacts`),
    ])
      .then(([reportResponse, canvasResponse]) => {
        setReport(reportResponse.data);
        setCanvasArtifacts(canvasResponse.data.timeline);
      })
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "Could not load webinar report."))
      .finally(() => setIsLoading(false));
  }, [webinarId]);

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, index) => <SkeletonCard key={index} className="h-40 rounded-xl" />)}
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-6">
        <h1 className="text-xl font-semibold text-foreground">Webinar Report</h1>
        <p className="mt-2 text-sm text-destructive">{error ?? "The webinar report is not available yet."}</p>
        <Link to={webinarId ? `/webinars/${webinarId}/analytics` : "/webinars"} className="mt-4 inline-flex text-sm text-primary hover:underline">
          Back to Webinar Analytics
        </Link>
      </div>
    );
  }

  const funnel = report.attendanceFunnel;
  const topModerationReasons = report.moderationSummary
    ? Object.entries(report.moderationSummary.reasonCodeCounts)
      .sort((left, right) => right[1] - left[1])
      .slice(0, 6)
    : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to={`/webinars/${report.webinarId}/analytics`} className="text-sm text-primary hover:underline">
            ← Back to Webinar Analytics
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-foreground">{report.title} Report</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Generated {formatDateTime(report.generatedAt)} from the mounted webinar intelligence seam.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Registered" value={String(funnel.registered)} />
        <MetricCard label="Joined Live" value={String(funnel.joined)} />
        <MetricCard label="Stayed 50%" value={String(funnel.stayed50Percent)} />
        <MetricCard label="Stayed to End" value={String(funnel.stayedToEnd)} detail={`Duration ${formatDuration(report.durationSeconds)}`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">Attendance Funnel</h2>
          <div className="mt-4 space-y-3">
            {[
              { label: "Registered", count: funnel.registered, base: funnel.registered || 1 },
              { label: "Joined Live", count: funnel.joined, base: funnel.registered || 1 },
              { label: "Stayed 50%", count: funnel.stayed50Percent, base: funnel.joined || 1 },
              { label: "Stayed to End", count: funnel.stayedToEnd, base: funnel.joined || 1 },
            ].map((step) => {
              const pct = Math.round((step.count / step.base) * 100);
              return (
                <div key={step.label}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-foreground">{step.label}</span>
                    <span className="text-muted-foreground">{step.count} ({Number.isFinite(pct) ? pct : 0}%)</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.max(Math.min(pct, 100), 0)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">Peak Engagement Moment</h2>
          {report.peakEngagementMoment ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-foreground">{report.peakEngagementMoment.summary}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <MetricCard label="Attendees" value={String(report.peakEngagementMoment.attendeeCount)} />
                <MetricCard label="Engagement Score" value={String(report.peakEngagementMoment.score)} />
                <MetricCard label="Chat" value={String(report.peakEngagementMoment.chatCount)} />
                <MetricCard label="Polls + Reactions" value={String(report.peakEngagementMoment.pollVotes + report.peakEngagementMoment.reactions)} />
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">No minute-level engagement data was available for this webinar.</p>
          )}
        </section>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">CTA Performance</h2>
          {report.ctaPerformance.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">No CTAs were shown during this webinar.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="pb-2">CTA</th>
                    <th className="pb-2">Type</th>
                    <th className="pb-2">Clicks</th>
                    <th className="pb-2">Conversions</th>
                    <th className="pb-2">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {report.ctaPerformance.map((cta) => (
                    <tr key={cta.ctaId}>
                      <td className="py-2 text-foreground">{cta.label}</td>
                      <td className="py-2 text-muted-foreground">{cta.type}</td>
                      <td className="py-2 text-foreground">{cta.clicks}</td>
                      <td className="py-2 text-foreground">{cta.conversions}</td>
                      <td className="py-2 text-foreground">{formatCurrency(cta.revenueCents, "USD")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">Top Questions</h2>
          {report.topQuestions.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">No audience questions were captured for this webinar.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {report.topQuestions.map((question) => (
                <div key={question.id} className="rounded-lg border border-border/70 bg-background px-3 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm text-foreground">{question.question}</p>
                      <p className="mt-1 text-xs text-muted-foreground">by {question.askedBy}</p>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      <div>{question.upvotes} upvotes</div>
                      <div>{question.answered ? "Answered" : "Needs follow-up"}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {report.moderationSummary ? (
        <section className="rounded-xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Moderation Evidence</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Held queue, attendee reports, classifier fallback, and raid-mode evidence captured during the live session.
              </p>
            </div>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard label="Held Messages" value={String(report.moderationSummary.totalHeld)} />
            <MetricCard
              label="Queue Decisions"
              value={String(report.moderationSummary.approved + report.moderationSummary.rejected + report.moderationSummary.undone)}
              detail={`${report.moderationSummary.approved} approved · ${report.moderationSummary.rejected} rejected · ${report.moderationSummary.undone} undone`}
            />
            <MetricCard
              label="Open Reports"
              value={String(report.moderationSummary.attendeeReports.open)}
              detail={`${report.moderationSummary.attendeeReports.resolved} resolved · ${report.moderationSummary.attendeeReports.dismissed} dismissed`}
            />
            <MetricCard
              label="Fallbacks / Raids"
              value={`${report.moderationSummary.classifierFallbackTriggered} / ${report.moderationSummary.raidEpisodes}`}
              detail="Classifier fallback events / raid episodes"
            />
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <div className="rounded-lg border border-border/70 bg-background p-4">
              <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Reason Codes</h3>
              {topModerationReasons.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">No moderation reason codes were captured.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {topModerationReasons.map(([reason, count]) => (
                    <div key={reason} className="flex items-center justify-between gap-3 rounded-md border border-border/60 px-3 py-2 text-sm">
                      <span className="font-medium text-foreground">{reason.replace(/_/g, " ")}</span>
                      <span className="text-muted-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-border/70 bg-background p-4">
              <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Moderator Activity</h3>
              {report.moderationSummary.topActors.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">No moderation actions were logged.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {report.moderationSummary.topActors.map((actor) => (
                    <div key={actor.actorId} className="flex items-center justify-between gap-3 rounded-md border border-border/60 px-3 py-2 text-sm">
                      <span className="font-medium text-foreground">{actor.actorId}</span>
                      <span className="text-muted-foreground">{actor.actions} actions</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      ) : null}

      <section className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Canvas Artifact Timeline</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Persisted presenter and attendee artifacts with authorship, board visibility, and replay timestamps.
            </p>
          </div>
        </div>

        {canvasArtifacts.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No persisted canvas artifacts were captured for this webinar.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {canvasArtifacts.map((artifact) => (
              <div key={artifact.id} className="rounded-lg border border-border/70 bg-background px-3 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm text-foreground">{artifact.summary}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {artifact.boardTitle} · {artifact.boardAudience === "presenter" ? "Private staging" : "Shared viewer"} · {artifact.createdBy}
                    </p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <div>{artifact.timestampSeconds == null ? "Live-only timestamp" : `${Math.floor(artifact.timestampSeconds / 60)}m ${artifact.timestampSeconds % 60}s`}</div>
                    <div>{artifact.interactionCount} interactions</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-foreground">Recommended Follow-Ups</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {report.recommendedFollowUps.map((item) => (
            <div key={item} className="rounded-lg border border-border/70 bg-background px-3 py-3 text-sm text-foreground">
              {item}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
